<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select class="input-label col-span-12 md:col-span-2" v-model="selectedValue">
        <el-option key="quickView" :label="$t('views.common.listView.quickView')" value="quickView">
        </el-option>
        <el-option key="allItems" :label="$t('views.common.listView.allItems')" value="allItems">
        </el-option>
        <el-option
          key="deletedItems"
          :label="$t('views.common.listView.deletedItems')"
          value="deletedItems"
        >
        </el-option>
        <el-option
          key="export"
          :label="$t('views.common.listView.selectedRows.export')"
          value="export"
        >
        </el-option>

        <el-option
          v-if="selectedValue != 'deletedItems'"
          key="delete"
          :label="$t('views.common.listView.selectedRows.delete')"
          value="delete"
        >
        </el-option>
        <el-option
          v-if="selectedValue == 'deletedItems'"
          key="restore"
          :label="$t('views.common.listView.selectedRows.restore')"
          value="restore"
        >
        </el-option>
      </el-select>

      <el-input
        :placeholder="$t('views.common.listView.searchRows')"
        class="w-full col-span-12 md:col-span-5"
        :prefix-icon="Search"
        v-model="searchTerm"
      />

      <div class="w-full flex items-center gap-2 col-span-12 md:col-span-5 justify-stretch">
        <div class="flex gap-2 grow">
          <horizontal-button
            :title="$t('views.appointments.appointments')"
            @click.prevent="$router.push($Route.APPOINTMENTS)"
            v-if="$can('read', '/customer/appointments')"
          />
          <horizontal-button
            :title="t('newVetService')"
            @click.prevent="$router.push($Route.VET_SERVICES_NEW)"
            v-if="$can('POST', '/customer/vet-services')"
          />
        </div>
      </div>
    </div>

    <ag-grid-vue
      v-if="rowModelType"
      class="ag-theme-alpine mt-4 md:mt-0"
      :columnDefs="columnDefs"
      rowSelection="multiple"
      :enable-rtl="$ctx.getDir() === 'rtl'"
      style="height: 85vh"
      @cell-double-clicked="onCellDoubleClicked"
      :defaultColDef="defaultColDef"
      :rowData="rowData"
      :pagination="selectedValue == 'allItems'"
      :paginationAutoPageSize="selectedValue == 'allItems'"
      :onGridReady="onGridReady"
      :rowBuffer="rowBuffer"
      :rowModelType="rowModelType"
      :cacheBlockSize="cacheBlockSize"
      :cacheOverflowSize="cacheOverflowSize"
      :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
      :infiniteInitialRowCount="infiniteInitialRowCount"
      :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>

    <div class="mt-2">{{ $t('views.common.listView.count') }}: {{ serviceCount }}</div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry
} from 'ag-grid-community'
import HorizontalButton from '@/components/HorizontalButton.vue'

import VetService, { VetServicesPaginated, vetServiceI18nMessages } from '@/models/VetService'
import { customerI18nMessages } from '@/models/customer/Customer'
import { useI18n } from 'vue-i18n'
import { Search } from '@element-plus/icons-vue'
import { onBeforeMount } from 'vue'
import { formatPostgresDate } from '@/utils/date'
import { useStorage } from '@vueuse/core'
import { useSettingsStore } from '@/stores/settingsStore'
import { appointmentI18nMessages } from '@/models/customer/Appointment'

ModuleRegistry.registerModules([InfiniteRowModelModule])

export default defineComponent({
  name: 'vet-services',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...vetServiceI18nMessages.en,
          ...customerI18nMessages.en,
          ...appointmentI18nMessages.en
        },
        ar: {
          ...vetServiceI18nMessages.ar,
          ...customerI18nMessages.ar,
          ...appointmentI18nMessages.ar
        }
      }
    })

    return { t, Search, useSettingsStore }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: VetService[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)

    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,

      searchTerm: useStorage('vet-services-search-term', ''),
      totalResultCount: useStorage('vet-services-total-result-count', 0),
      selectedValue: 'quickView',
      gridApi: undefined as GridApi | undefined,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache,
      serviceCount: 0
    }
  },

  components: {
    AgGridVue,
    HorizontalButton
  },

  async beforeMount() {
    try {
      this.serviceCount = (
        await this.$http.get<{ count: number }>('/helpers/count?model=vet_services')
      ).count
    } catch (error: any) {
      this.$alertModal.showDanger({ title: error.title, body: error.body })
    }

    this.defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      minWidth: 150
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },
      {
        headerName: `${this.t('vetService')} #`,
        field: 'externalID',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        valueGetter: (params) => {
          const service = params.node?.data as VetService
          if (!service) return ''
          return service.externalID
        }
      },
      {
        headerName: this.$t('models.common.date'),
        field: 'createdAt',
        cellStyle: { textAlign: 'center' },
        valueGetter: (params) => {
          if (!params.data) return ''
          const service = params.node?.data as VetService
          if (service.createdAt) {
            return formatPostgresDate(String(service.createdAt), 'DD/MM/YYYY')
          }
          return ''
        }
      },
      {
        headerName: this.$t('models.common.time'),
        field: 'createdAt',
        cellStyle: { direction: 'ltr !important', textAlign: 'center' },
        valueGetter: (params) => {
          if (!params.data) return ''
          const service = params.node?.data as VetService
          if (service.createdAt) {
            return formatPostgresDate(String(service.createdAt), 'hh:mm A')
          }
          return ''
        }
      },
      {
        headerName: this.t('pet'),
        valueGetter: (params) => {
          const service = params.node?.data as VetService
          if (!service) return ''
          return service.petName
        }
      },
      {
        headerName: this.t('type'),
        valueGetter: (params) => {
          const service = params.node?.data as VetService
          if (!service || !service.pet) return ''
          return service.pet.type
        }
      },
      {
        headerName: this.t('customer'),
        valueGetter: (params) => {
          const service = params.node?.data as VetService
          if (!service) return ''
          return service.customerName
        }
      },
      {
        headerName: this.t('phone'),
        valueGetter: (params) => {
          const service = params.node?.data as VetService
          if (!service || !service.customer) return ''
          if ('phone' in service.customer) {
            return service.customer.phone
          }
          return ''
        }
      },
      {
        headerName: this.t('vet'),
        valueGetter: (params) => {
          const service = params.node?.data as VetService
          if (!service) return ''
          return service.vetName
        }
      }
    ]
  },

  methods: {
    async onGridReady(params: any) {
      this.gridApi = params.api

      if (this.selectedValue == 'allItems' || this.selectedValue == 'deletedItems') {
        await this.updateRowData(this.selectedValue == 'deletedItems')
      } else {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
      }
    },

    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          const url = VetService.ENDPOINT
          const urlParams = new URLSearchParams()
          urlParams.set('startRow', String(params.startRow))
          urlParams.set('endRow', String(params.endRow))

          if (params.sortModel.length) {
            urlParams.set('colId', params.sortModel[0].colId)
            urlParams.set('sort', params.sortModel[0].sort)
          }

          if (Object.keys(params.filterModel).length) {
            urlParams.set('query', params.filterModel['-'].filter)
          }

          if (this.searchTerm) {
            urlParams.set('query', this.searchTerm)
          }

          try {
            const res = await this.$http.get<VetServicesPaginated>(url + `?${urlParams.toString()}`)
            this.rowData = res.data.map((service) => VetService.from(service))
            this.gridApi?.hideOverlay()

            const lastRow = res.total
            if (this.rowData.length == 0) {
              this.totalResultCount = 0
            } else {
              this.totalResultCount = res.total
            }
            params.successCallback(this.rowData, lastRow)
          } catch (error: any) {
            this.gridApi?.hideOverlay()
            params.failCallback()
            this.$alertModal.showDanger({ title: error.title, body: error.body })
          }
        }
      }
      return dataSource
    },

    async updateRowData(softDeleted: boolean = false) {
      this.gridApi?.showLoadingOverlay()

      try {
        const data = await this.$http.get<VetService[]>(VetService.ENDPOINT)
        if (softDeleted) {
          this.rowData = data
            .map((service) => VetService.from(service))
            .filter((service) => service.deletedAt != null)
        } else {
          this.rowData = data
            .map((service) => VetService.from(service))
            .filter((service) => service.deletedAt == null)
        }
        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error: any) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },

    onCellDoubleClicked(event: any) {
      const service = event.data as VetService
      if (service && service.id) {
        this.$router.push(`/vet-services/service/${service.id}`)
      }
    },

    filterItems() {
      if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    },

    filterResult() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({ '-': { filter: this.searchTerm } })
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },

    async delete(services: VetService[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))) {
        try {
          await this.$http.delete(VetService.ENDPOINT, { data: services })
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.deleteSuccess')
          })
        } catch (error: any) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    },

    async restoreItems(services: VetService[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.restoreConfirm'))) {
        try {
          await this.$http.post('/helpers/restore-deleted', {
            data: services,
            table: 'vet_services'
          })
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.restoreSuccess')
          })
        } catch (error: any) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    }
  },

  watch: {
    searchTerm() {
      this.filterResult()
    },

    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export' || oldValue == 'restore') {
        return
      }

      if (
        this.selectedValue === 'delete' ||
        this.selectedValue === 'export' ||
        this.selectedValue === 'restore'
      ) {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            // send request to archive
            await this.delete(selected)
            if (oldValue == 'quickView') {
              this.gridApi?.refreshInfiniteCache()
            } else {
              await this.updateRowData()
            }
          } else if (this.selectedValue === 'restore') {
            // send request to restore
            await this.restoreItems(selected)
            await this.updateRowData(oldValue == 'deletedItems')
          } else {
            this.gridApi?.exportDataAsCsv({ onlySelected: true })
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      } else if (this.selectedValue == 'deletedItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      }
    }
  }
})
</script>

<style scoped>
</style>
